import type { ReactNode } from 'react';
import { Box, Text } from '@citibox-panel/ui';

export interface FieldRowProps {
  label: string;
  value: ReactNode;
}

// An empty value (null/undefined or a blank/whitespace-only string) renders as an em
// dash so a missing account or device field reads as "—" instead of a blank line.
function isBlank(value: ReactNode): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

// Label over value (stacked): the label in bold small-caps secondary text above the
// value in body text. Repeated across the account and device sections.
export function FieldRow({ label, value }: FieldRowProps) {
  const displayed = isBlank(value) ? '—' : value;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 0.5,
        // The overline ships a tall 2.66 line-height; collapse it so the value sits
        // directly under the label with no vertical gap between the two. Bold weight
        // makes the label read as the header above its value.
        '& .MuiTypography-overline': { lineHeight: 1.2, fontWeight: 'bold' },
      }}
    >
      <Text variant="overline" color="secondary">
        {label}
      </Text>
      <Text variant="body2">{displayed}</Text>
    </Box>
  );
}
